import { useNuxtApp } from '#app'
import { useBasketStore } from '~/stores/basket'
import DataLayer from '~/extensions/DataLayer'
import GtmEcommerceEvent from '~/models/DataLayer/GtmEcommerceEvent'
import { GtmEcommerceEventTypeEnum } from '~/models/DataLayer/GtmEcommerceEventTypeEnum'
import AddToBasketInput from '~/models/Basket/AddToBasketInput'
import ViewProductResponse from '~/models/Api/ViewProductResponse'
import GtmProduct from '~/models/Product/GtmProduct'

const affectStore = (productId: number, amount: number) => {
    const basketStore = useBasketStore()

    basketStore.changeAmount(productId, amount)
    basketStore.setTotalAmount(basketStore.calculateTotalAmount())
    basketStore.setProductsPrice(basketStore.calculateProductsPrice())
    basketStore.calculateFreeDelivery(basketStore.calculateAfterDiscountPrice())
}

const sleep = (ms: number) => new Promise((_resolve) => setTimeout(_resolve, ms))

let timeoutId: NodeJS.Timeout
let idInProgress: number
let maxIdWaiting = 0

export const addProductToBasket = async (addToBasketInput: AddToBasketInput) => {
    let { showPreBasket, productIdReferrer, fullProduct } = addToBasketInput
    const {
        skipPushToDataLayer,
        product,
        basketProduct,
        productId,
        amount,
        itemListName,
        carouselName,
        listTitle,
        listType,
        listPosition,
        forceShowPreBasket,
        addonSaleBehavior,
    } = addToBasketInput
    const currentId: number = Date.now()
    maxIdWaiting = currentId
    const basketStore = useBasketStore()
    const nuxtApp = useNuxtApp()

    basketStore.setVoucherErrorMessage('')

    const amountBefore = basketStore.productAmounts[productId] || 0
    showPreBasket = (amountBefore === 0 && showPreBasket) || forceShowPreBasket === true
    affectStore(productId, amount)

    let gtmProduct: GtmProduct | null = null
    if (product) {
        gtmProduct = transformProductToGtmProduct(product)
    } else if (fullProduct) {
        gtmProduct = transformFullProductToGtmProduct(fullProduct)
    } else if (basketProduct) {
        gtmProduct = transformBasketProductToGtmProduct(basketProduct)
    } else {
        const viewProductResponse: ViewProductResponse = await nuxtApp.$repository.getProductDetailDataAccess(productId)
        fullProduct = viewProductResponse.product
        gtmProduct = transformFullProductToGtmProduct(fullProduct)
    }

    setTimeout(() => {
        const diff = amount - amountBefore
        if (showPreBasket && (product || fullProduct)) {
            basketStore.setCustomAddonSaleCarouselsAreLoading(true)
            basketStore.setAddonSaleAreasAreLoading(true)

            nuxtApp.$bus.$emit('showModal', 'modal-prebasket')
            nuxtApp.$bus.$emit('show-prebasket', product || fullProduct)
        }

        let listName: string | null = null
        if (listTitle && listType) {
            listName = DataLayer.getListName(listTitle, listType)
        }

        productIdReferrer = productIdReferrer === productId ? null : productIdReferrer

        if (!skipPushToDataLayer) {
            const ecommerceEvent = {
                type: diff > 0 ? GtmEcommerceEventTypeEnum.AddToCart : GtmEcommerceEventTypeEnum.RemoveFromCart,
                products: [gtmProduct],
                listName,
                itemListName,
                carouselName,
                targetListPosition: listPosition,
                productAmount: Math.abs(diff),
                productIdReferrer,
            } as GtmEcommerceEvent

            DataLayer.pushEcommerceEventToDataLayer(ecommerceEvent)
        }
    }, 0)
    clearTimeout(timeoutId)

    timeoutId = setTimeout(
        async () => {
            // Ensures order of requests
            if (idInProgress) {
                // Other request in progress
                while (true) {
                    if (maxIdWaiting > currentId) {
                        // newer one is waiting => cancel current request
                        return
                    }

                    if (!idInProgress) {
                        // No request in progress => break and send request to API
                        break
                    }

                    await sleep(200)
                }
            }

            idInProgress = currentId
            const products: { productId: number; amount: number; voucherCode: string | null; hasAddonSalePrice?: boolean }[] = []
            // actual product has to be last one
            for (const [key, value] of Object.entries(basketStore.productAmounts)) {
                if (productId !== parseInt(key)) {
                    products.push({
                        productId: parseInt(key),
                        amount: value,
                        voucherCode: null,
                    })
                }
            }
            const productOrFullProduct = product || fullProduct
            products.push({
                productId,
                amount,
                voucherCode:
                    amount > 0 &&
                    productOrFullProduct &&
                    productOrFullProduct.productCodeSale &&
                    productOrFullProduct.productCodeSale.voucherCode.length > 0
                        ? productOrFullProduct.productCodeSale?.voucherCode || null
                        : null,
                hasAddonSalePrice: addonSaleBehavior,
            })
            const uidCookie = useUidCookie()
            let uid = uidCookie.value || null
            const accessToken = useAccessTokenCookie().value || ''
            if (!uid) {
                uid = await nuxtApp.$repository.generateBasketUid(accessToken)
            }
            uidCookie.value = uid

            if (showPreBasket) {
                nuxtApp.$repository.getProductAddonSaleAreas(productId).then((response) => {
                    basketStore.setAddonSaleAreas(response)
                    basketStore.setAddonSaleAreasAreLoading(false)
                })
                nuxtApp.$repository.getCustomAddonSaleCarousels().then((response) => {
                    basketStore.setCustomAddonSaleCarousels(response)
                    basketStore.setCustomAddonSaleCarouselsAreLoading(false)
                })
            }

            const response = await nuxtApp.$repository.upsertProductBasket(uid, products, accessToken, showPreBasket)
            uidCookie.value = response.basket.uid
            basketStore.setUid(uidCookie.value)
            basketStore.setVoucherErrorMessage(response.basket?.voucherErrorMessage)
            if (productOrFullProduct) {
                basketStore.setActiveProducts(
                    await nuxtApp.$repository.getGiftEventProducts(
                        uid,
                        (productOrFullProduct.giftActions || []).map((ga) => ga.giftEventId)
                    )
                )
            }
            if (maxIdWaiting === currentId) {
                // fetch data only if current request is last one
                const basketData = await basketStore.fetchData(false)
                if (basketData && maxIdWaiting === currentId) {
                    basketStore.init(basketData)
                }

                nuxtApp.$bus.$emit('productAddedToBasket')
            }
            idInProgress = 0
        },
        showPreBasket ? 0 : 500
    )
}
